import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { toast } from "@/store/toast.store";
import { useCommissionSettings, useUpdateCommissionSettings } from "@/features/commission/useCommission";
import { formatINR } from "@/utils/format";

const toPct = (fraction: number) => String(Math.round(fraction * 10000) / 100);

/** Weekly boundary + commission % — commission is taken at withdrawal, not per ride. */
export function CommissionPolicyCard() {
  const settings = useCommissionSettings();
  const update = useUpdateCommissionSettings();
  const [boundary, setBoundary] = useState("");
  const [below, setBelow] = useState("");
  const [above, setAbove] = useState("");
  const [dueLimit, setDueLimit] = useState("");

  useEffect(() => {
    if (!settings.data) return;
    setBoundary(String(settings.data.weeklyBoundary));
    setBelow(toPct(settings.data.rateBelowBoundary));
    setAbove(toPct(settings.data.rateAboveBoundary));
    setDueLimit(String(settings.data.dueLimit));
  }, [settings.data]);

  const valid =
    boundary !== "" && below !== "" && above !== "" && dueLimit !== "" && Number(dueLimit) >= 0 &&
    Number(boundary) >= 0 && [below, above].every((v) => Number(v) >= 0 && Number(v) <= 100);

  const save = () =>
    update.mutate(
      {
        weeklyBoundary: Number(boundary),
        rateBelowBoundary: Number(below) / 100,
        rateAboveBoundary: Number(above) / 100,
        dueLimit: Number(dueLimit),
      },
      { onSuccess: () => toast.success("Commission policy updated") },
    );

  return (
    <form
      className="card flex flex-col gap-4 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save();
      }}
    >
      <div>
        <h3 className="text-sm font-semibold text-ink">Commission policy</h3>
        <p className="text-xs text-ink-faint">
          Commission is taken when a driver withdraws, from their UPI balance. The rate is chosen
          by the driver&apos;s total rides this week (cash + UPI).
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label="Weekly boundary (₹)" hint="Week total (cash + UPI) that switches the rate">
          <Input type="number" min="0" step="100" value={boundary} onChange={(e) => setBoundary(e.target.value)} />
        </FormField>
        <FormField label="Commission up to boundary (%)" hint="Applied while week total ≤ boundary">
          <Input type="number" min="0" max="100" step="0.5" value={below} onChange={(e) => setBelow(e.target.value)} />
        </FormField>
        <FormField label="Commission above boundary (%)" hint="Applied on the whole week total once exceeded">
          <Input type="number" min="0" max="100" step="0.5" value={above} onChange={(e) => setAbove(e.target.value)} />
        </FormField>
        <FormField label="Block above due (₹)" hint="Driver can't go online until unpaid commission is at or below this">
          <Input type="number" min="0" step="100" value={dueLimit} onChange={(e) => setDueLimit(e.target.value)} />
        </FormField>
      </div>
      {valid && (
        <p className="text-xs text-ink-muted">
          Example: a driver with {formatINR(Number(boundary) + 500)} of rides this week pays {above}% ={" "}
          {formatINR(((Number(boundary) + 500) * Number(above)) / 100)}, deducted from their UPI withdrawal.
        </p>
      )}
      <div>
        <Button type="submit" disabled={!valid || settings.isLoading} loading={update.isPending}>
          Save policy
        </Button>
      </div>
    </form>
  );
}
